import { Utils } from '@/types/utils';
import { nanoid } from 'nanoid';
import {
    ComponentProps,
    ReactNode,
    Suspense,
    lazy,
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react';
import { useLocation } from 'react-router';

import {
    DialogActionsContext,
    DialogStateContext,
    IActiveDialog,
    IDialogOptions,
} from './DialogContext';
import { ManagedAxleDialogProps } from './ManagedAxleDialog';

/**********************************************************
 * Dialog Manager - a high-level component and context that
 * allows us to invoke dialogs from anywhere in the app without
 * having to manage dialog state manually.
 *
 * The DialogManager expects to be used only for those
 * dialog components that use <ManagedAxleDialog />, as
 * that allows us to get the nice slide-in/-out animation
 * without the parent component having to manage the isOpen
 * state to do so.
 *
 * Onboarding a dialog into this manager involves:
 * 1. Creating your new dialog with <ManagedAxleDialog />
 * 2. Adding a new entry to the DialogType enum below.
 * 3. Adding a new entry to the DialogLazyComponents record below.
 *
 * Then, in the component that wants to open the dialog, you can
 * use the `openDialog` function provided by the `useDialog`
 * hook to open the dialog.
 *
 * ----------------------------------------------------------
 * Example:
 * [in DialogManager.tsx]
 * export enum DialogType {
 *   ...
 *   MyNewDialog,
 *   ...
 * }
 *
 * const DialogLazyComponents = {
 *   ...
 *   [DialogType.MyNewDialog]: lazy(() => import('./path/to/MyNewDialog').then((module) => ({
 *     default: module.MyNewDialog,
 *   })),
 *   ...
 * }
 *
 * [in the component that wants to open the dialog]
 * const { openDialog } = useDialog();
 * openDialog(DialogType.MyNewDialog, {
 *   // Any props you want to pass to the dialog
 *   // ...
 * });
 * ----------------------------------------------------------
 *
 * The dialog components are loaded lazily, in case we want to
 * do code-splitting in the future.
 *
 * Implementation loosely adapted from:
 * https://hackernoon.com/mastering-modal-dialogs-in-react-like-a-pro
 **********************************************************/

export enum DialogType {
    // Common Utility Dialogs
    Confirm,

    //Clinician Details
    DeactivateProfile,

    // Clinician Teams
    ClinicianTeamDetails,
    EditClinicianTeam,
    CreateClinicianTeam,

    // Clinician Schedules
    FilterClinicianSchedules,

    // Orders
    FilterOrders,

    // Patients
    CreatePatient,
    PatientDetailsSummary,
    EditPatientCareTeam,
    EditPatientCareTeamRecommendation,
    EditPatientCareTeamDatesRoles,
    EditPatientNotes,
    EditPatientTimeslotPreferenceList,
    EditPatientTimeslotPreferences,
    CreatePatientTimeslotPreferenceItem,
    EditPatientTimeslotPreferenceItem,
    CreatePatientVisit,

    // Patients (Legacy)
    LegacyEditPatient,

    // Shifts
    CreateShift,
    EditShift,

    // Shift Blocks
    CreateShiftBlock,
    EditShiftBlock,

    // Shift Block Requests
    ReviewShiftBlockRequest,

    // Territories
    CreateTerritory,
    EditTerritory,
    AddOrRemoveTerritoryZipcodes,

    // Services
    ServicesFilter,
    CreateService,
    EditService,

    // Service Bundles
    CreateServiceBundle,
    EditServiceBundle,

    // Demand Connections
    EditDemandConnection,

    // Dashboard
    CustomizeDashboardContainers,

    // Tasks
    EditTask,

    // Qualifications
    ManageQualifications,
    CreateQualification,
    EditQualification,

    // Operating Hour Blocks
    CreateOperatingHoursBlock,
    DeleteOperatingHoursBlock,

    // Documents
    UploadDocument,

    // Visits
    BulkEditVisitStateOutcome,

    // Visit Orders
    EndVisitOrder,
    EditVisitOrder,

    // Dashboard Users
    CreateDashboardUser,
    EditDashboardUser,

    // Clinicians
    FilterClinicians,
    CreateClinician,
    EditClinician,
    EditClinicianById,

    // Supply Connections
    CreateConnection,
    EditSupplyConnection,

    // Route Optimizer
    RoutingFilters,
    ClinicianFilters,
    TentativelyEditUnscheduledVisit,
    OptimizeRoutes,

    // VisitRequests
    EditVisitRequest,

    // Visits
    BulkCancelVisits,
    EditVisit,
    EditVisitServices,
    EditFulfillmentWindow,
    FilterVisits,
    MarkVisitAsMissed,
    ReassignVisits,
    SchedulingConflict,

    // Visits (Legacy)
    LegacyExportVisits,

    DateDecisionSupport,
    AssignmentClinicianDecisionSupport,
    AssignmentDateDecisionSupport,

    // Urgent Booking
    UrgentBookingFlow,
}

export { useActiveDialogs, useDialog } from './DialogContext';

// Root-level component that provides the context and handles rendering active dialogs.
export const DialogProvider = ({ children }: { children: ReactNode }) => {
    const { openDialog, activeDialogs } = useDialogProvider();

    // Memoized separately from activeDialogs so its identity is stable across
    // dialog open/close. Consumers of `useDialog()` therefore never re-render
    // when a dialog is opened or closed.
    const actions = useMemo(() => ({ openDialog }), [openDialog]);

    return (
        <DialogActionsContext.Provider value={actions}>
            <DialogStateContext.Provider value={activeDialogs}>
                {children}
                {typeof window !== 'undefined' && activeDialogs.length ? (
                    <>
                        {activeDialogs.map(({ id, component: LazyDialog }) => (
                            <Suspense fallback={null} key={id}>
                                <LazyDialog />
                            </Suspense>
                        ))}
                    </>
                ) : null}
            </DialogStateContext.Provider>
        </DialogActionsContext.Provider>
    );
};

// Tells us where to find the dialog components for each dialog type.
const DialogLazyComponents = {
    [DialogType.DeactivateProfile]: lazy(() =>
        import('../components/profiles/dialogs/DeactivateProfileDialogSwitch').then((module) => ({
            default: module.DeactivateProfileDialogSwitch,
        })),
    ),

    // Clinician Teams
    [DialogType.ClinicianTeamDetails]: lazy(() =>
        import('../pages/QhpDetailsPage/ClinicianTeamDetailsDialogSwitch').then((module) => ({
            default: module.ClinicianTeamDetailsDialogSwitch,
        })),
    ),
    [DialogType.CreateClinicianTeam]: lazy(() =>
        import('../pages/SupplyPage/Qhps/CreateClinicianTeamDialogSwitch').then((module) => ({
            default: module.CreateClinicianTeamDialogSwitch,
        })),
    ),
    [DialogType.EditClinicianTeam]: lazy(() =>
        import('../pages/SupplyPage/Qhps/EditClinicianTeamDialogSwitch').then((module) => ({
            default: module.EditClinicianTeamDialogSwitch,
        })),
    ),
    [DialogType.FilterClinicianSchedules]: lazy(() =>
        import('../pages/SchedulePage/ScheduleFilterDialogSwitch').then((module) => ({
            default: module.ScheduleFilterDialogSwitch,
        })),
    ),
    [DialogType.DateDecisionSupport]: lazy(() =>
        import('../components/decisionSupport/DateDecisionSupportDialogSwitch').then((module) => ({
            default: module.DateDecisionSupportDialogSwitch,
        })),
    ),
    [DialogType.AssignmentClinicianDecisionSupport]: lazy(() =>
        import('../components/decisionSupport/AssignmentClinicianDecisionSupportDialogSwitch').then(
            (module) => ({
                default: module.AssignmentClinicianDecisionSupportDialogSwitch,
            }),
        ),
    ),
    [DialogType.AssignmentDateDecisionSupport]: lazy(() =>
        import('../components/decisionSupport/AssignmentDateDecisionSupportDialogSwitch').then(
            (module) => ({
                default: module.AssignmentDateDecisionSupportDialogSwitch,
            }),
        ),
    ),

    // Common Utility Dialogs
    [DialogType.Confirm]: lazy(() =>
        import('../components/layout/AxleConfirmDialogSwitch').then((module) => ({
            default: module.ManagedAxleConfirmDialogSwitch,
        })),
    ),

    // Orders
    [DialogType.FilterOrders]: lazy(() =>
        import('../components/orders/OrderFilterDialogSwitch').then((module) => ({
            default: module.OrderFilterDialogSwitch,
        })),
    ),

    // Patients
    [DialogType.CreatePatient]: lazy(() =>
        import('../components/patients/dialogs/CreatePatientDialogSwitch').then((module) => ({
            default: module.CreatePatientDialogSwitch,
        })),
    ),
    [DialogType.PatientDetailsSummary]: lazy(() =>
        import(
            '../components/patients/dialogs/PatientDetailsSummary/PatientDetailsSummaryDialogSwitch'
        ).then((module) => ({
            default: module.PatientDetailsSummaryDialogSwitch,
        })),
    ),
    [DialogType.EditPatientCareTeam]: lazy(() =>
        import(
            '../components/patients/dialogs/EditPatientCareTeam/EditPatientCareTeamDialogSwitch'
        ).then((module) => ({
            default: module.EditPatientCareTeamDialogSwitch,
        })),
    ),
    [DialogType.EditPatientCareTeamRecommendation]: lazy(() =>
        import(
            '../components/patients/dialogs/EditPatientCareTeam/EditPatientCareTeamRecommendationDialogSwitch'
        ).then((module) => ({
            default: module.EditPatientCareTeamRecommendationDialogSwitch,
        })),
    ),
    [DialogType.EditPatientCareTeamDatesRoles]: lazy(() =>
        import(
            '../components/patients/dialogs/EditPatientCareTeam/v2/EditPatientCareTeamDatesRolesDialog/EditPatientCareTeamDatesRolesDialogMount'
        ).then((module) => ({
            default: module.EditPatientCareTeamDatesRolesDialogMount,
        })),
    ),
    [DialogType.EditPatientNotes]: lazy(() =>
        import('../components/patients/dialogs/EditPatientNotesDialogSwitch').then((module) => ({
            default: module.EditPatientNotesDialogSwitch,
        })),
    ),
    [DialogType.EditPatientTimeslotPreferenceList]: lazy(() =>
        import('../components/patients/dialogs/EditPatientTimeslotPreferenceListDialogSwitch').then(
            (module) => ({
                default: module.EditPatientTimeslotPreferenceListDialogSwitch,
            }),
        ),
    ),
    [DialogType.EditPatientTimeslotPreferences]: lazy(() =>
        import(
            '../components/patients/dialogs/StandaloneEditPatientTimeslotPreferenceListDialogSwitch'
        ).then((module) => ({
            default: module.StandaloneEditPatientTimeslotPreferenceListDialogSwitch,
        })),
    ),
    [DialogType.CreatePatientTimeslotPreferenceItem]: lazy(() =>
        import(
            '../components/patients/dialogs/CreatePatientTimeslotPreferenceItemDialogSwitch'
        ).then((module) => ({
            default: module.CreatePatientTimeslotPreferenceItemDialogSwitch,
        })),
    ),
    [DialogType.EditPatientTimeslotPreferenceItem]: lazy(() =>
        import('../components/patients/dialogs/EditPatientTimeslotPreferenceItemDialogSwitch').then(
            (module) => ({
                default: module.EditPatientTimeslotPreferenceItemDialogSwitch,
            }),
        ),
    ),
    [DialogType.CreatePatientVisit]: lazy(() =>
        import(
            '../components/patients/dialogs/CreatePatientVisit/CreatePatientVisitDialogSwitch'
        ).then((module) => ({
            default: module.CreatePatientVisitDialogSwitch,
        })),
    ),

    // Patients (Legacy)
    [DialogType.LegacyEditPatient]: lazy(() =>
        import('../pages/legacy/LegacyPatientDetailsPage/LegacyEditPatientDialogSwitch').then(
            (module) => ({
                default: module.LegacyEditPatientDialogSwitch,
            }),
        ),
    ),

    // Shifts
    [DialogType.CreateShift]: lazy(() =>
        import('../components/shifts/dialogs/CreateShiftDialogSwitch').then((module) => ({
            default: module.CreateShiftDialogSwitch,
        })),
    ),
    [DialogType.EditShift]: lazy(() =>
        import('../components/shifts/dialogs/EditShiftDialogSwitch').then((module) => ({
            default: module.EditShiftDialogSwitch,
        })),
    ),

    // Shift Blocks
    [DialogType.CreateShiftBlock]: lazy(() =>
        import('../components/shifts/dialogs/CreateShiftBlockDialogSwitch').then((module) => ({
            default: module.CreateShiftBlockDialogSwitch,
        })),
    ),
    [DialogType.EditShiftBlock]: lazy(() =>
        import('../components/shifts/dialogs/EditShiftBlockDialogSwitch').then((module) => ({
            default: module.EditShiftBlockDialogSwitch,
        })),
    ),

    // Shift Block Requests
    [DialogType.ReviewShiftBlockRequest]: lazy(() =>
        import(
            '../pages/SchedulePage/ClinicianScheduleRequestsTab/ReviewShiftBlockRequestDialogSwitch'
        ).then((module) => ({
            default: module.ReviewShiftBlockRequestDialogSwitch,
        })),
    ),

    // Territories
    [DialogType.CreateTerritory]: lazy(() =>
        import('../pages/TerritoriesPage/CreateTerritoryDialogSwitch').then((module) => ({
            default: module.CreateTerritoryDialogSwitch,
        })),
    ),
    [DialogType.EditTerritory]: lazy(() =>
        import('../pages/TerritoriesPage/EditTerritoryDialogSwitch').then((module) => ({
            default: module.EditTerritoryDialogSwitch,
        })),
    ),
    [DialogType.AddOrRemoveTerritoryZipcodes]: lazy(() =>
        import('../pages/TerritoriesPage/AddOrRemoveZipcodesDialogSwitch').then((module) => ({
            default: module.AddOrRemoveZipcodesDialogSwitch,
        })),
    ),

    // Services
    [DialogType.ServicesFilter]: lazy(() =>
        import('../pages/DemandPage/Services/ServicesFilterDialogSwitch').then((module) => ({
            default: module.ServicesFilterDialogSwitch,
        })),
    ),
    [DialogType.CreateService]: lazy(() =>
        import('../pages/DemandPage/Services/CreateServiceDialogSwitch').then((module) => ({
            default: module.CreateServiceDialogSwitch,
        })),
    ),
    [DialogType.EditService]: lazy(() =>
        import('../pages/DemandPage/Services/EditServiceDialogSwitch').then((module) => ({
            default: module.EditServiceDialogSwitch,
        })),
    ),

    // Service Bundles
    [DialogType.CreateServiceBundle]: lazy(() =>
        import('../pages/DemandPage/ServiceBundles/_helpers/CreateServiceBundleDialogSwitch').then(
            (module) => ({
                default: module.CreateServiceBundleDialogSwitch,
            }),
        ),
    ),
    [DialogType.EditServiceBundle]: lazy(() =>
        import('../pages/DemandPage/ServiceBundles/_helpers/EditServiceBundleDialogSwitch').then(
            (module) => ({
                default: module.EditServiceBundleDialogSwitch,
            }),
        ),
    ),

    // Demand Connections
    [DialogType.EditDemandConnection]: lazy(() =>
        import('../pages/DemandPage/DemandConnections/EditDemandConnectionDialogSwitch').then(
            (module) => ({
                default: module.EditDemandConnectionDialogSwitch,
            }),
        ),
    ),

    // Dashboard
    [DialogType.CustomizeDashboardContainers]: lazy(() =>
        import('../pages/DashboardPage/containers/ContainersCustomizationDialogSwitch').then(
            (module) => ({
                default: module.ContainersCustomizationDialogSwitch,
            }),
        ),
    ),

    // Operating Hour Blocks
    [DialogType.CreateOperatingHoursBlock]: lazy(() =>
        import('../pages/blocks/CreateBlockDialogSwitch').then((module) => ({
            default: module.CreateBlockDialogSwitch,
        })),
    ),
    [DialogType.DeleteOperatingHoursBlock]: lazy(() =>
        import('../pages/blocks/DeleteBlockDialogSwitch').then((module) => ({
            default: module.DeleteBlockDialogSwitch,
        })),
    ),

    // Documents
    [DialogType.UploadDocument]: lazy(() =>
        import('../components/documents/DocumentUploadDialogSwitch').then((module) => ({
            default: module.DocumentUploadDialogSwitch,
        })),
    ),

    // Visits
    [DialogType.BulkEditVisitStateOutcome]: lazy(() =>
        import('../components/visits/BulkEditVisitStateOutcomeDialogSwitch').then((module) => ({
            default: module.BulkEditVisitStateOutcomeDialogSwitch,
        })),
    ),

    // Visit Orders
    [DialogType.EndVisitOrder]: lazy(() =>
        import('../components/visitOrders/EndVisitOrderDialogSwitch').then((module) => ({
            default: module.EndVisitOrderDialogSwitch,
        })),
    ),
    [DialogType.EditVisitOrder]: lazy(() =>
        import('../components/visitOrders/OrderBookingInfo/EditVisitOrderDialogSwitch').then(
            (module) => ({
                default: module.EditVisitOrderDialogSwitch,
            }),
        ),
    ),

    // Tasks
    [DialogType.EditTask]: lazy(() =>
        import('../components/EditTaskDialogSwitch').then((module) => ({
            default: module.EditTaskDialogSwitch,
        })),
    ),

    // Qualifications
    [DialogType.ManageQualifications]: lazy(() =>
        import('../components/formFields/QualificationDialogSwitches').then((module) => ({
            default: module.ManageQualificationsDialogSwitch,
        })),
    ),
    [DialogType.CreateQualification]: lazy(() =>
        import('../components/formFields/QualificationDialogSwitches').then((module) => ({
            default: module.CreateQualificationDialogSwitch,
        })),
    ),
    [DialogType.EditQualification]: lazy(() =>
        import('../components/formFields/QualificationDialogSwitches').then((module) => ({
            default: module.EditQualificationDialogSwitch,
        })),
    ),

    // Dashboard Users
    [DialogType.CreateDashboardUser]: lazy(() =>
        import('../pages/SettingsPage/CreateDashboardUserDialogSwitch').then((module) => ({
            default: module.CreateDashboardUserDialogSwitch,
        })),
    ),
    [DialogType.EditDashboardUser]: lazy(() =>
        import('../pages/SettingsPage/EditDashboardUserDialogSwitch').then((module) => ({
            default: module.EditDashboardUserDialogSwitch,
        })),
    ),

    // Clinicians
    [DialogType.FilterClinicians]: lazy(() =>
        import('../pages/SupplyPage/Qhps/QhpFilterDialogSwitch').then((module) => ({
            default: module.QhpFilterDialogSwitch,
        })),
    ),
    [DialogType.CreateClinician]: lazy(() =>
        import('../pages/SupplyPage/Qhps/CreateQhpDialogSwitch').then((module) => ({
            default: module.CreateQhpDialogSwitch,
        })),
    ),
    [DialogType.EditClinician]: lazy(() =>
        import('../pages/QhpDetailsPage/EditQhpDialogSwitch').then((module) => ({
            default: module.EditQhpDialogSwitch,
        })),
    ),
    [DialogType.EditClinicianById]: lazy(() =>
        import('../pages/QhpDetailsPage/EditQhpByIdDialog').then((module) => ({
            default: module.EditQhpByIdDialog,
        })),
    ),

    // Supply Connections
    [DialogType.CreateConnection]: lazy(() =>
        import('../pages/SupplyPage/SupplyConnections/CreateConnectionDialogSwitch').then(
            (module) => ({
                default: module.CreateConnectionDialogSwitch,
            }),
        ),
    ),
    [DialogType.EditSupplyConnection]: lazy(() =>
        import('../pages/SupplyPage/SupplyConnections/EditSupplyConnectionDialogSwitch').then(
            (module) => ({
                default: module.EditSupplyConnectionDialogSwitch,
            }),
        ),
    ),

    // Route Optimizer
    [DialogType.RoutingFilters]: lazy(() =>
        import('../pages/RouteOptimizerPage/RoutingFiltersDialogSwitch').then((module) => ({
            default: module.RoutingFiltersDialogSwitch,
        })),
    ),
    [DialogType.ClinicianFilters]: lazy(() =>
        import('../pages/RouteOptimizerPage/ClinicianFiltersDialogSwitch').then((module) => ({
            default: module.ClinicianFiltersDialogSwitch,
        })),
    ),
    [DialogType.OptimizeRoutes]: lazy(() =>
        import('../pages/RouteOptimizerPage/OptimizeRoutesDialogSwitch').then((module) => ({
            default: module.OptimizeRoutesDialogSwitch,
        })),
    ),
    [DialogType.TentativelyEditUnscheduledVisit]: lazy(() =>
        import('../pages/RouteOptimizerPage/TentativelyEditUnscheduledVisitDialogSwitch').then(
            (module) => ({
                default: module.TentativelyEditUnscheduledVisitDialogSwitch,
            }),
        ),
    ),

    // Visit Requests
    [DialogType.EditVisitRequest]: lazy(() =>
        import('../components/visitRequests/EditVisitRequestDialog').then((module) => ({
            default: module.EditVisitRequestDialog,
        })),
    ),

    // Visits
    [DialogType.BulkCancelVisits]: lazy(() =>
        import('../components/visits/dialogs/BulkCancelVisits/BulkCancelVisitsDialogSwitch').then(
            (module) => ({
                default: module.BulkCancelVisitsDialogSwitch,
            }),
        ),
    ),
    [DialogType.EditVisit]: lazy(() =>
        import('../components/visits/EditVisitDialogSwitch').then((module) => ({
            default: module.EditVisitDialogSwitch,
        })),
    ),
    [DialogType.EditVisitServices]: lazy(() =>
        import('../components/visits/dialogs/EditVisitServices/EditVisitServicesDialogSwitch').then(
            (module) => ({
                default: module.EditVisitServicesDialogSwitch,
            }),
        ),
    ),
    [DialogType.EditFulfillmentWindow]: lazy(() =>
        import(
            '../components/visits/dialogs/EditFulfillmentWindow/EditFulfillmentWindowDialogSwitch'
        ).then((module) => ({
            default: module.EditFulfillmentWindowDialogSwitch,
        })),
    ),
    [DialogType.FilterVisits]: lazy(() =>
        import('../components/visits/VisitFilterDialogSwitch').then((module) => ({
            default: module.VisitFilterDialogSwitch,
        })),
    ),
    [DialogType.MarkVisitAsMissed]: lazy(() =>
        import('../components/visits/MarkVisitMissedDialogSwitch').then((module) => ({
            default: module.MarkVisitMissedDialogSwitch,
        })),
    ),
    [DialogType.ReassignVisits]: lazy(() =>
        import('../components/visits/dialogs/ReassignVisits/ReassignVisitsDialog').then(
            (module) => ({
                default: module.ReassignVisitsDialog,
            }),
        ),
    ),
    [DialogType.SchedulingConflict]: lazy(() =>
        import('../pages/VisitBooking/scheduling/SchedulingConflictDialogSwitch').then(
            (module) => ({
                default: module.SchedulingConflictDialogSwitch,
            }),
        ),
    ),

    // Urgent Booking
    [DialogType.UrgentBookingFlow]: lazy(() =>
        import('../components/patients/dialogs/UrgentBookingFlow/UrgentBookingFlow').then(
            (module) => ({
                default: module.UrgentBookingFlowDialog,
            }),
        ),
    ),

    // Visits (Legacy)
    [DialogType.LegacyExportVisits]: lazy(() =>
        import('../pages/legacy/LegacyVisitsPage/LegacyExportVisitsDialogSwitch').then(
            (module) => ({
                default: module.LegacyExportVisitsDialogSwitch,
            }),
        ),
    ),
};

// Exported so DialogContext.ts can reference the lazy component map in its type definitions.
export type DialogLazyComponentsType = typeof DialogLazyComponents;

const DIALOG_DEFAULT_OPTION_CONFIG: IDialogOptions = {
    closeOnNavigationChange: true,
};

const DialogDefaultOptions: Partial<Record<DialogType, IDialogOptions>> = {};

const getDialogDefaultOptions = (type: DialogType) => {
    return {
        ...DIALOG_DEFAULT_OPTION_CONFIG,
        ...(DialogDefaultOptions[type] ?? {}),
    };
};

// Internally-used hook to initialize the root provider.
const useDialogProvider = () => {
    const [activeDialogs, setActiveDialogs] = useState<IActiveDialog[]>([]);
    const location = useLocation();
    const isMounted = useRef(false);

    const unmountDialog = useCallback(
        (id: string) => {
            setActiveDialogs((prev) => prev.filter((d) => d.id !== id));
        },
        [setActiveDialogs],
    );

    // Listen for navigation changes and close dialogs that are configured to do so.
    useEffect(() => {
        if (!isMounted.current) {
            isMounted.current = true;
            return;
        }
        activeDialogs.forEach(({ id, options }) => {
            if (options.closeOnNavigationChange) {
                unmountDialog(id);
            }
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [location]);

    const open = useCallback(
        <
            DialogT extends DialogType,
            DialogComponentPropsT extends ComponentProps<(typeof DialogLazyComponents)[DialogT]>,
        >(
            type: DialogT,
            props: Omit<DialogComponentPropsT, keyof ManagedAxleDialogProps>,
            options?: IDialogOptions,
        ) => {
            const id = nanoid();
            const Component = DialogLazyComponents[type];
            const dialogOptions = {
                ...getDialogDefaultOptions(type),
                ...options,
            };

            // Ignores repeat opens of a type that is already active, e.g. a double click
            // that lands before the lazy dialog renders its backdrop.
            setActiveDialogs((prev) =>
                prev.some((d) => d.type === type)
                    ? prev
                    : [
                          ...prev,
                          {
                              id,
                              options: dialogOptions,
                              type,
                              component: () => (
                                  // TODO(jessica): See if we can tighten this up, though, it doesn't seem necessarily important to do so,
                                  // in that openDialog already gives proper hints/errors inferred from the underlying dialog type.
                                  <Component
                                      {...(props as Utils.UNSAFE_ANY)}
                                      onClose={() => unmountDialog(id)}
                                  />
                              ),
                          },
                      ],
            );
        },
        [unmountDialog],
    );

    // `open` is a stable callback (memoized on `unmountDialog`), while
    // `activeDialogs` is state. DialogProvider wraps each in its own context so
    // that opening a dialog doesn't re-render components that only need `open`.
    return { openDialog: open, activeDialogs };
};
